// Per-day desk / parking picker — RON-5466 Phase 2. Prototype components: `ResourcePicker`,
// `ResourceReview`, `resStats`, `resLabel`.
//
// A multi-day visit holds or releases a desk and a parking space independently per day, matching
// `tblvisitor_visit_resource` (one row per resource per day, UNIQUE on VisitID+date+kind).
//
// IMPORTANT: this only records INTENT. Nothing is booked here. On submit the backend hands each
// day off to Ronspot's existing ad-hoc booking flow and stores the returned
// `ronspot_booking_ref`. We do not rebuild booking (RON-5466 constraint, db-schema §0/§5).

import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { daysOf } from "../visitorDateUtils.js";

const EMPTY_DAY = { desk: false, parking: false };

const dayValue = (resources, day) => ({ ...EMPTY_DAY, ...(resources?.[day] || {}) });

// Which days have this resource requested, given the current date range.
export const resourceDays = (resources, startDate, endDate, kind) =>
  daysOf(startDate, endDate).filter((day) => dayValue(resources, day)[kind]);

const ResourceChip = ({ label, isOn, onToggle, modifier }) => (
  <button
    type="button"
    className={`visitor-chip visitor-chip--${modifier}${
      isOn ? " visitor-chip--on" : ""
    }`}
    aria-pressed={isOn}
    onClick={onToggle}
  >
    {isOn ? `✓ ${label}` : label}
  </button>
);

const VisitorResourcePicker = ({
  startDate,
  endDate,
  value,
  onChange,
  canAssignDesk,
  canAssignParking,
}) => {
  const { t } = useTranslation("common");

  // Gated by the employee's Hosts rights — an employee with neither never sees this card.
  if (!canAssignDesk && !canAssignParking) return null;

  const days = daysOf(startDate, endDate);
  if (days.length === 0) return null;

  const isAllOn = (kind) => days.every((day) => dayValue(value, day)[kind]);

  const setDay = (day, kind, next) => {
    onChange({
      ...value,
      [day]: { ...dayValue(value, day), [kind]: next },
    });
  };

  const setAll = (kind, next) => {
    const updated = { ...value };
    days.forEach((day) => {
      updated[day] = { ...dayValue(value, day), [kind]: next };
    });
    onChange(updated);
  };

  const isMultiDay = days.length > 1;

  return (
    <div className="visitor-card">
      <p className="visitor-card__title">{t("react_visitors_resources_title")}</p>
      <p className="visitor-card__hint">{t("react_visitors_resources_hint")}</p>

      {isMultiDay && (
        <div className="visitor-resources__all">
          <span className="visitor-resources__all-label">
            {t("react_visitors_resources_all_days").replace("%s", String(days.length))}
          </span>
          {canAssignDesk && (
            <ResourceChip
              label={t("react_visitors_desk")}
              modifier="desk"
              isOn={isAllOn("desk")}
              onToggle={() => setAll("desk", !isAllOn("desk"))}
            />
          )}
          {canAssignParking && (
            <ResourceChip
              label={t("react_visitors_parking")}
              modifier="parking"
              isOn={isAllOn("parking")}
              onToggle={() => setAll("parking", !isAllOn("parking"))}
            />
          )}
        </div>
      )}

      <ul className="visitor-resources__days">
        {days.map((day) => {
          const current = dayValue(value, day);

          return (
            <li key={day} className="visitor-resources__day">
              <span className="visitor-resources__day-label">
                {formatDateToEEEddMMM(day)}
              </span>
              <span className="visitor-resources__day-chips">
                {canAssignDesk && (
                  <ResourceChip
                    label={t("react_visitors_desk")}
                    modifier="desk"
                    isOn={current.desk}
                    onToggle={() => setDay(day, "desk", !current.desk)}
                  />
                )}
                {canAssignParking && (
                  <ResourceChip
                    label={t("react_visitors_parking")}
                    modifier="parking"
                    isOn={current.parking}
                    onToggle={() => setDay(day, "parking", !current.parking)}
                  />
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default VisitorResourcePicker;
